import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useStore } from '../stores/store';
import { AppRoute } from '../constants/route';
import { DEFAULT_COUNTRY } from '../stores/localizationsStore';

export const useInitLocale = () => {
  const location = useLocation();

  const {
    localizationsStore: {
      selectLocalizationByCode,
      selectCountryByCode,
      selectLocale,
      setPreferredCountryAliasCode,
      setPreferredLanguageCode,
      availableCountries,
      selectedCountry,
      selectedLocalization,
      preferredCountry,
    },
  } = useStore();

  // Please do not replace with useLayoutEffect.
  // useLayoutEffect causes bug when root page is still displayed, even though URL is updated to e.g. /us-en/portal
  useEffect(() => {
    const path = location.pathname;

    // Skip locale auto-redirect on OIDC callback/logout endpoints so the library
    // can finish processing the code/state parameters before the URL changes.
    const isOauthPage =
      path.startsWith(`/${AppRoute.OAUTH2_CODE}`) ||
      path.startsWith(`/${AppRoute.OAUTH2_LOGOUT}`) ||
      path.startsWith(`/${AppRoute.OAUTH2_RENEW}`);

    if (isOauthPage) {
      return;
    }

    const matchesLocalePath = path.match(/^\/([a-z]{2})-([a-z]{2})(\/.*)?/i);

    if (matchesLocalePath) {
      const [, countryAliasCode, localizationCode] = matchesLocalePath;
      selectCountryByCode(countryAliasCode);
      setPreferredCountryAliasCode(countryAliasCode);
      selectLocalizationByCode(localizationCode);
      setPreferredLanguageCode(localizationCode);
      return;
    }

    // Following code is required only for Root page locale preselection (when locale is not in the URL).
    // No need to execute it if locale is present in store.
    if (selectedCountry && selectedLocalization) return;

    if (preferredCountry) {
      selectLocale(preferredCountry);
      return;
    }

    // If locale is not in the url try to obtain country from geoflare and lang from browser settings (via selectLocale)
    const fetchUserCountry = async () => {
      const defaultCountry = availableCountries?.find(
        (c) => c.alias === DEFAULT_COUNTRY.alias
      );
      try {
        const res = await fetch('https://geo-flare.meest.shopping/');
        const data = await res.json();

        const country = availableCountries?.find(
          (c) => c.alias === data.country
        );

        selectLocale(country ?? defaultCountry);
      } catch (e) {
        selectLocale(defaultCountry);
      }
    };
    fetchUserCountry();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    location.pathname,
    availableCountries,
    selectCountryByCode,
    selectLocalizationByCode,
  ]);
};
